import React from 'react';
import { Pin } from 'lucide-react';
import { AppItem, useAppFavorites } from '@/hooks/app-library/use-app-favorites';
import { getIconByName } from '@/hooks/app-library/utils';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';

interface PinnedAppGhostProps {
  app: AppItem;
  currentDropTarget?: 1 | 2 | 3 | 4 | null;
  originalPosition?: 1 | 2 | 3 | 4 | null;
}

/**
 * Enhanced ghost component for pinned app drag overlay
 * Matches the current AppCard design with glass morphism
 * Shows dynamic position based on current drop target
 */
const PinnedAppGhost: React.FC<PinnedAppGhostProps> = ({ 
  app, 
  currentDropTarget = null, 
  originalPosition = null 
}) => {
  const isMobile = useIsMobile();
  const IconComponent = getIconByName(app.icon_name);
  const { isAppFavorited, handleToggleFavorite, getAppFavoriteCount, isLoading } = useAppFavorites();

  const handleToggleAppFavorite = async (itemId: string) => {
    await handleToggleFavorite(itemId);
  };

  // Determine which position to show in the badge
  const getDisplayPosition = (): 1 | 2 | 3 | 4 => {
    // If we're hovering over a drop target, show that position
    if (currentDropTarget) {
      return currentDropTarget;
    }
    // Otherwise, show the original position or default to 1
    return originalPosition || 1;
  };

  const displayPosition = getDisplayPosition();
  const positions = ['1st', '2nd', '3rd', '4th'];

  return (
    <div 
      className={cn(
        "relative group cursor-grabbing select-none w-full transform-gpu",
        "bg-gradient-to-br from-white via-white to-gray-50/80",
        "dark:from-slate-800 dark:via-slate-800 dark:to-slate-900/80",
        "backdrop-blur-xl border border-white/20 dark:border-slate-700/40",
        "rounded-xl shadow-2xl",
        "transition-all duration-300 ease-out",
        // Enhanced shadow and glow for ghost state
        "shadow-purple-500/20 dark:shadow-purple-400/30",
        "ring-2 ring-purple-500/30 dark:ring-purple-400/40"
      )}
      style={{ 
        willChange: 'transform, opacity',
        transform: 'scale(1.05) rotate(3deg)', // Slight scale and rotation for ghost effect
      }}
    >
      <div 
        className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} flex flex-col h-full`} 
        style={{ 
          willChange: 'contents',
          borderRadius: 'inherit'
        }}
      >
        {/* Header section - matching AppCard structure */}
        <div className="flex items-start justify-between mb-2 md:mb-3">
          <div className="flex items-center gap-2 md:gap-3">
            <div className={cn(
              "p-2 md:p-3 rounded-lg",
              "bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900",
              "group-hover:from-brandBlue/10 group-hover:to-brandPurple/10",
              "flex items-center justify-center"
            )}>
              <IconComponent 
                className="text-brandBlue dark:text-brandPurple" 
                size={isMobile ? 20 : 28} 
              />
            </div>
            <div className="flex flex-row items-center gap-2">
              <h3 className="font-semibold text-sm md:text-lg text-foreground truncate">
                {app.display_name || app.name || 'Untitled'}
              </h3>
            </div>
          </div>
          
          {/* Pin position badge with dynamic feedback */}
          <div className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border shadow-lg backdrop-blur-lg transition-all duration-200",
            currentDropTarget && currentDropTarget !== originalPosition
              ? "bg-blue-500/30 dark:bg-blue-400/30 border-blue-400/50 dark:border-blue-300/50" // Highlight when position changes
              : "bg-white/20 dark:bg-black/20 border-white/20 dark:border-transparent" // Default styling
          )}>
            <div className="flex items-center justify-center">
              <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
            </div>
            <span className="text-sm font-medium text-white">{positions[displayPosition - 1]}</span>
          </div>
        </div>

        {/* Content section - matching AppCard */}
        <div className="flex-1 mb-2 md:mb-3">
          <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 md:line-clamp-3">
            {app.description || 'No description available'}
          </p>
        </div>

        {/* Footer section - simplified for ghost */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 md:gap-2">
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 dark:bg-green-400/10 rounded-full border border-green-200/50 dark:border-green-800/30">
              <div className="w-1.5 h-1.5 bg-green-500 dark:bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Available</span>
            </div>
          </div>
          
                     <PersistentFavoriteButton
             itemId={app.id}
             isFavorite_prop={isAppFavorited(app.id)}
             favoriteCount_prop={getAppFavoriteCount(app.id)}
             onToggleFavorite={handleToggleAppFavorite}
             className="opacity-70" // Reduced opacity for ghost state
           />
        </div>
      </div>
    </div>
  );
};

export default PinnedAppGhost; 